import { useState, useCallback, type ReactNode } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { resolveUrl } from "@/lib/queryClient";
import { Trophy, Loader2, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";

interface Competition {
  competition_id: number;
  nom_tournoi: string | null;
  niveau: string | null;
  categorie: string | null;
  partenaire: string | null;
  rang_obtenu: number | null;
  points: number | null;
  expiration: string | null;
}

interface Participant {
  idCrm: string | null;
  prenom: string;
  nom: string;
  dernier_rang: number | null;
  partenaire_rang: number | null;
  partenaire: string | null;
  rang_obtenu: number | null;
  points: number | null;
}

interface ParticipantsData {
  nom_tournoi?: string;
  participants: Participant[];
}

// La date FFT (expiration) est le dernier mois de prise en compte : le tournoi reste
// valide pendant ce mois et n'expire que le mois suivant → on avance d'un mois avant de
// calculer les mois restants (règle +1 mois de la référence PadelResa).
function monthsUntil(d: string | null): number | null {
  if (!d) return null;
  const eff = new Date(d);
  eff.setMonth(eff.getMonth() + 1);
  return Math.round((eff.getTime() - Date.now()) / (1000 * 60 * 60 * 24 * 30.44));
}

export default function TournamentHistoryCard({
  idCrm,
  licence,
  name,
  trigger,
}: {
  idCrm?: string | null;
  licence?: string | null;
  name: string;
  // Déclencheur personnalisé (ex. le nom du joueur cliquable). Défaut : bouton « Historique ».
  trigger?: ReactNode;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [competitions, setCompetitions] = useState<Competition[] | null>(null);
  const [expandedTournoi, setExpandedTournoi] = useState<number | null>(null);
  const [participants, setParticipants] = useState<Record<number, ParticipantsData>>({});

  const hasKey = !!idCrm || !!licence;

  const loadCompetitions = useCallback(async () => {
    if (competitions) return;
    const url = idCrm
      ? `/api/players/${idCrm}/competitions`
      : `/api/players/by-licence/${licence}/competitions`;
    try {
      const res = await fetch(resolveUrl(url));
      const data = await res.json();
      setCompetitions(Array.isArray(data?.competitions) ? data.competitions : []);
    } catch {
      setCompetitions([]);
    }
  }, [competitions, idCrm, licence]);

  function handleOpenChange(next: boolean) {
    setOpen(next);
    if (next) {
      loadCompetitions();
    } else {
      setExpandedTournoi(null);
    }
  }

  async function toggleTournoi(competitionId: number) {
    if (expandedTournoi === competitionId) {
      setExpandedTournoi(null);
      return;
    }
    setExpandedTournoi(competitionId);
    if (!participants[competitionId]) {
      try {
        const res = await fetch(resolveUrl(`/api/competitions/${competitionId}/participants`));
        const data = await res.json();
        setParticipants(prev => ({ ...prev, [competitionId]: data }));
      } catch {
        setParticipants(prev => ({ ...prev, [competitionId]: { participants: [] } }));
      }
    }
  }

  // Tri identique à la référence : non-expirés d'abord (points DESC), expirés en fin.
  const isExpired = (c: Competition) => {
    const mm = monthsUntil(c.expiration);
    return mm != null && mm <= 0 ? 1 : 0;
  };
  const sorted = competitions
    ? [...competitions].sort(
        (a, b) => isExpired(a) - isExpired(b) || (b.points ?? -1) - (a.points ?? -1),
      )
    : null;

  // 12 meilleurs tournois non-expirés (assiette FFT) — triés points DESC.
  const countedIds = new Set(
    (sorted ?? [])
      .filter(c => c.points != null && (monthsUntil(c.expiration) ?? 0) > 0)
      .slice(0, 12)
      .map(c => c.competition_id),
  );

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        {trigger ?? (
          <button
            type="button"
            disabled={!hasKey}
            onClick={(e) => e.stopPropagation()}
            className="inline-flex items-center gap-1 text-[10px] font-semibold text-[#001489] dark:text-blue-400 hover:underline disabled:opacity-40 disabled:no-underline"
          >
            <Trophy className="w-3 h-3" />
            {t("players.tab.tournamentHistory", "Historique des tournois")}
          </button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Trophy className="w-4 h-4 text-[#001489] dark:text-blue-400" />
            {t("players.tab.tournamentHistory", "Historique des tournois")}
            <span className="font-normal text-muted-foreground">· {name}</span>
          </DialogTitle>
        </DialogHeader>

        {!competitions ? (
          <div className="flex justify-center py-10">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : competitions.length === 0 ? (
          <p className="text-center text-sm text-muted-foreground py-10">
            {t("players.tab.noTournaments", "Aucune compétition enregistrée")}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-muted-foreground border-b border-border">
                  <th className="py-2 pr-2 font-semibold">{t("players.tab.tournament", "Tournoi")}</th>
                  <th className="py-2 px-2 font-semibold text-right">{t("players.tab.points", "Points")}</th>
                  <th className="py-2 px-2 font-semibold">{t("players.tab.partner", "Partenaire")}</th>
                  <th className="py-2 px-2 font-semibold text-right">{t("players.tab.rank", "Rang")}</th>
                  <th className="py-2 pl-2 font-semibold text-right">{t("players.tab.expiration", "Expiration")}</th>
                </tr>
              </thead>
              <tbody>
                {sorted!.map((c) => {
                  const m = monthsUntil(c.expiration);
                  const counted = countedIds.has(c.competition_id);
                  const isTourn = expandedTournoi === c.competition_id;
                  const pData = participants[c.competition_id];
                  // Surlignage des 12 comptés : rouge si expire bientôt, orange à 2 mois.
                  const countedBg = counted
                    ? m != null && m <= 1
                      ? "bg-red-50 dark:bg-red-950/30"
                      : m === 2
                      ? "bg-orange-50 dark:bg-orange-950/30"
                      : "bg-blue-50/60 dark:bg-blue-950/20"
                    : "";
                  return [
                    <tr
                      key={c.competition_id}
                      onClick={() => toggleTournoi(c.competition_id)}
                      className={`border-b border-border/50 cursor-pointer hover:bg-muted/50 ${countedBg}`}
                      title={t("players.tab.viewParticipants", "Voir les participants")}
                    >
                      <td className="py-2 pr-2">
                        <span className="inline-flex items-center gap-1">
                          <ChevronRight
                            className={`w-3 h-3 transition-transform ${isTourn ? "rotate-90" : ""}`}
                          />
                          {c.nom_tournoi || "—"}
                        </span>
                      </td>
                      <td className="py-2 px-2 text-right font-medium">{c.points ?? "—"}</td>
                      <td className="py-2 px-2 truncate max-w-[140px]">
                        {c.partenaire || t("players.tab.anonymousPlayer", "Joueur anonyme")}
                      </td>
                      <td className="py-2 px-2 text-right">{c.rang_obtenu ?? "—"}</td>
                      {m == null ? (
                        <td className="py-2 pl-2 text-right">—</td>
                      ) : (
                        <td
                          className={`py-2 pl-2 text-right ${m <= 2 ? "text-red-500 font-medium" : ""}`}
                        >
                          {m <= 0 ? t("players.tab.expired", "Expiré") : `${m} ${t("players.tab.months", "mois")}`}
                        </td>
                      )}
                    </tr>,
                    isTourn ? (
                      <tr key={`p-${c.competition_id}`} className="bg-muted/30">
                        <td colSpan={5} className="p-2">
                          {!pData ? (
                            <div className="flex justify-center py-4">
                              <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                            </div>
                          ) : (
                            <ParticipantsTable data={pData} currentIdCrm={idCrm ?? null} />
                          )}
                        </td>
                      </tr>
                    ) : null,
                  ];
                })}
              </tbody>
            </table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ParticipantsTable({
  data,
  currentIdCrm,
}: {
  data: ParticipantsData;
  currentIdCrm: string | null;
}) {
  const { t } = useTranslation();
  const raw = data.participants ?? [];
  if (raw.length === 0) {
    return (
      <p className="text-center text-xs text-muted-foreground py-3">
        {t("players.tab.noParticipants", "Aucun participant trouvé")}
      </p>
    );
  }

  // Écarter les entrées placeholder (noms purement numériques), dédupliquer par paire,
  // en priorisant la ligne du joueur courant pour qu'elle survive à la dédup.
  const isPlaceholder = (p: Participant) => /^[\d\s]+$/.test((p.prenom + " " + p.nom).trim());
  const all = raw.filter(p => !isPlaceholder(p));
  const prioritized = [...all].sort(
    (a, b) =>
      (b.idCrm === currentIdCrm ? 1 : 0) - (a.idCrm === currentIdCrm ? 1 : 0),
  );
  const seen = new Set<string>();
  const deduped = prioritized.filter(p => {
    const key = [p.prenom + " " + p.nom, p.partenaire || ""].sort().join("|");
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  deduped.sort((a, b) => (a.rang_obtenu ?? 999) - (b.rang_obtenu ?? 999));

  // Têtes de série par poids de paire (somme des rangs actuels) croissant.
  const poidsPaire = (p: Participant) =>
    p.dernier_rang != null && p.partenaire_rang != null
      ? p.dernier_rang + p.partenaire_rang
      : null;
  const ranked = deduped
    .map(p => ({ p, poids: poidsPaire(p) }))
    .filter(x => x.poids != null)
    .sort((a, b) => (a.poids as number) - (b.poids as number));
  const tsMap = new Map<string | null, number>(ranked.map((x, i) => [x.p.idCrm, i + 1]));

  return (
    <table className="w-full text-[11px]">
      <thead>
        <tr className="text-left text-muted-foreground">
          <th className="py-1 pr-2 font-semibold text-right">{t("players.tab.rank", "Rang")}</th>
          <th className="py-1 px-2 font-semibold">{t("players.tab.player", "Joueur")}</th>
          <th className="py-1 px-2 font-semibold">{t("players.tab.partner", "Partenaire")}</th>
          <th className="py-1 px-2 font-semibold">{t("players.tab.teamRank", "Rang équipe")}</th>
          <th className="py-1 pl-2 font-semibold text-right">{t("players.tab.points", "Points")}</th>
        </tr>
      </thead>
      <tbody>
        {deduped.map((p, pi) => {
          const isCurrent = p.idCrm != null && p.idCrm === currentIdCrm;
          const poids = poidsPaire(p);
          const ts = tsMap.get(p.idCrm);
          return (
            <tr
              key={pi}
              className={isCurrent ? "bg-blue-100 dark:bg-blue-900/40 font-medium" : ""}
            >
              <td className="py-1 pr-2 text-right">{p.rang_obtenu ?? "—"}</td>
              <td className="py-1 px-2">
                {p.prenom} {p.nom}
              </td>
              <td className="py-1 px-2">
                {p.partenaire || t("players.tab.anonymousPlayer", "Joueur anonyme")}
              </td>
              <td className="py-1 px-2">
                {poids != null ? (
                  <span>
                    TS{ts} <span className="text-muted-foreground">({poids})</span>
                  </span>
                ) : p.dernier_rang != null ? (
                  `${p.dernier_rang} + ?`
                ) : (
                  "—"
                )}
              </td>
              <td className="py-1 pl-2 text-right">{p.points ?? "—"}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
